/**
 * One recipient: name, channel (text or Slack), mobile (masked, stored E.164 —
 * required for text, optional for Slack), Slack channel (required for Slack),
 * roles (all or a pick), active, remove. The summary line under the heading
 * says where this person is reached: "(713) 555-0101" or "#hiring-alerts".
 * Every edit reports which field it touched so the page can drop that error.
 */
import { useId, useState } from 'react';
import { MessageSquare, Slack, Trash2 } from 'lucide-react';
import type { Role } from '@/features/pipeline/types';
import { formatE164, formatPhoneInput, toE164 } from './phone';
import { slackChannelLabel } from './settings-api';
import { SlackChannelPicker, type ChannelsState } from './SlackChannelPicker';
import type { Recipient, RecipientErrors, RecipientField } from './types';

const inputClass =
  'mt-1 block min-h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900 aria-[invalid=true]:border-danger';

export function RecipientRow({
  recipient,
  roles,
  channels,
  errors,
  onChange,
  onRemove,
}: {
  recipient: Recipient;
  roles: Role[];
  channels: ChannelsState;
  errors: RecipientErrors | undefined;
  onChange: (next: Recipient, touched: RecipientField) => void;
  onRemove: () => void;
}) {
  const id = useId();
  const [phoneText, setPhoneText] = useState(() => formatE164(recipient.phone_e164));
  // The field keeps what the person typed (digits still arriving), but when the saved value
  // changes underneath it — Discard, or a refetch — and no longer matches, show the saved value
  // (Stage 6 QA: Discard left a half-typed number in the box).
  const [seenPhone, setSeenPhone] = useState(recipient.phone_e164);
  if (recipient.phone_e164 !== seenPhone) {
    setSeenPhone(recipient.phone_e164);
    if (toE164(phoneText) !== recipient.phone_e164) setPhoneText(formatE164(recipient.phone_e164));
  }
  const slack = recipient.channel === 'slack';
  const allRoles = recipient.roles === 'all';
  const picked = new Set(allRoles ? [] : recipient.roles);
  const label = recipient.name.trim() || 'New recipient';
  const reach = slack ? slackChannelLabel(channels.channels, recipient.slack_channel_id) : formatE164(recipient.phone_e164) || '—';
  const ReachIcon = slack ? Slack : MessageSquare;

  const toggleRole = (slug: string, on: boolean) => {
    const next = new Set(picked);
    if (on) next.add(slug);
    else next.delete(slug);
    onChange({ ...recipient, roles: [...next] }, 'roles');
  };

  return (
    <li className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4" data-testid={`recipient-${recipient.id}`} aria-label={label}>
      <p className="mb-2 flex items-center gap-1.5 text-[12px] text-muted" data-testid="recipient-summary">
        <ReachIcon size={12} strokeWidth={2} aria-hidden />
        <span>
          {slack ? 'Slack' : 'Text'} · <span className="text-gray-700">{reach}</span>
        </span>
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="text-[13px]">
          <label htmlFor={`${id}-name`} className="font-medium text-gray-800">Name</label>
          <input id={`${id}-name`} value={recipient.name} onChange={(e) => onChange({ ...recipient, name: e.target.value }, 'name')} aria-invalid={errors?.name ? true : undefined} aria-describedby={errors?.name ? `${id}-name-err` : undefined} autoComplete="off" className={inputClass} />
          {errors?.name && <span id={`${id}-name-err`} role="alert" className="mt-1 block text-[12px] text-danger">{errors.name}</span>}
        </div>
        <div className="text-[13px]">
          <label htmlFor={`${id}-channel`} className="font-medium text-gray-800">Channel</label>
          <select id={`${id}-channel`} value={recipient.channel} onChange={(e) => onChange({ ...recipient, channel: e.target.value === 'slack' ? 'slack' : 'sms' }, 'channel')} className={inputClass}>
            <option value="sms">Text message (SMS)</option>
            <option value="slack">Slack channel</option>
          </select>
        </div>
        <div className="text-[13px]">
          <label htmlFor={`${id}-phone`} className="font-medium text-gray-800">{slack ? 'Mobile (optional)' : 'Mobile'}</label>
          <input
            id={`${id}-phone`}
            type="tel"
            inputMode="tel"
            value={phoneText}
            placeholder="(713) 555-0101"
            onChange={(e) => {
              const shown = formatPhoneInput(e.target.value);
              setPhoneText(shown);
              onChange({ ...recipient, phone_e164: toE164(shown) }, 'phone');
            }}
            aria-invalid={errors?.phone ? true : undefined}
            aria-describedby={errors?.phone ? `${id}-phone-err` : `${id}-phone-hint`}
            className={inputClass}
          />
          {errors?.phone ? (
            <span id={`${id}-phone-err`} role="alert" className="mt-1 block text-[12px] text-danger">{errors.phone}</span>
          ) : (
            <span id={`${id}-phone-hint`} className="mt-1 block text-[12px] text-muted">
              {recipient.phone_e164 ? `Saved as ${recipient.phone_e164}` : slack ? 'Kept on file; Slack recipients are not texted.' : 'US mobile, 10 digits.'}
            </span>
          )}
        </div>
        {slack && (
          <SlackChannelPicker
            id={`${id}-slack`}
            value={recipient.slack_channel_id}
            channels={channels}
            error={errors?.slack_channel}
            onChange={(slack_channel_id) => onChange({ ...recipient, slack_channel_id }, 'slack_channel')}
          />
        )}
        <div className="text-[13px]">
          <label htmlFor={`${id}-user`} className="font-medium text-gray-800">Innu user id (optional)</label>
          <input
            id={`${id}-user`}
            value={recipient.user_id ?? ''}
            onChange={(e) => onChange({ ...recipient, user_id: e.target.value.trim() || null }, 'user_id')}
            aria-describedby={`${id}-user-hint`}
            autoComplete="off"
            spellCheck={false}
            className={inputClass}
          />
          <span id={`${id}-user-hint`} className="mt-1 block text-[12px] text-muted">
            Matches the lead's assigned user so only they get nudged.
          </span>
        </div>
        <fieldset className="min-w-0 text-[13px]">
          <legend className="font-medium text-gray-800">Roles</legend>
          <label className="mt-1 flex min-h-10 items-center gap-2">
            <input type="checkbox" checked={allRoles} onChange={(e) => onChange({ ...recipient, roles: e.target.checked ? 'all' : [] }, 'roles')} className="h-4 w-4 accent-navy" />
            All roles
          </label>
          {!allRoles && (
            <div className="flex flex-wrap gap-x-4 gap-y-0.5">
              {roles.map((r) => (
                <label key={r.slug} className="flex min-h-10 items-center gap-2">
                  <input type="checkbox" checked={picked.has(r.slug)} onChange={(e) => toggleRole(r.slug, e.target.checked)} className="h-4 w-4 accent-navy" />
                  {r.title}
                </label>
              ))}
              {picked.size === 0 && <span className="self-center text-[12px] text-danger">No roles picked — this person gets nothing.</span>}
            </div>
          )}
        </fieldset>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <label className="flex min-h-10 items-center gap-2 text-[13px] text-gray-800">
          <input type="checkbox" role="switch" aria-checked={recipient.active} checked={recipient.active} onChange={(e) => onChange({ ...recipient, active: e.target.checked }, 'active')} className="h-4 w-4 accent-navy" />
          Active
        </label>
        <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className="inline-flex min-h-10 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-danger hover:bg-danger-bg">
          <Trash2 size={14} strokeWidth={2} aria-hidden /> Remove
        </button>
      </div>
    </li>
  );
}
